import {Component, DOCUMENT, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {MatIconModule} from '@angular/material/icon';
import {MatDialog} from '@angular/material/dialog';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Observable} from 'rxjs';
import {WaitlistService} from '../../waitlist.service';
import {WaitlistPortalPageDto, WaitlistPortalReservationDto, WaitlistPortalSlotDto} from '../../dtos/waitlist-portal-view.dto';
import {WaitlistConfirmData, WaitlistConfirmDialog} from '../../dialogs/confirm/waitlist-confirm.dialog';
import {WaitlistSettingsDialog} from '../../dialogs/settings/waitlist-settings.dialog';
import {SlotDetailsComponent} from '../slot-details/slot-details.component';
import {ErrorService} from '../../../../core/services/error.service';
import {CustomDatePipe} from '../../../../shared/pipes/custom-date-pipe';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {LogoComponent} from '../../../../shared/components/logo/logo.component';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * The patient's waitlist page, reached from a link in a mail or text: no login and no navigation,
 * everything hangs off the `reference` in the route.
 */
@Component({
  selector: 'app-waitlist',
  imports: [MatIconModule, TranslocoPipe, CustomDatePipe, TextButton, IconButtonComponent, LogoComponent, SlotDetailsComponent],
  // also provided, so the confirmation texts format dates through the same pipe as the page
  providers: [CustomDatePipe],
  templateUrl: './waitlist.component.html',
  styleUrl: './waitlist.component.scss'
})
export class WaitlistComponent implements OnInit {
  route = inject(ActivatedRoute);
  waitlistService = inject(WaitlistService);
  errorService = inject(ErrorService);
  translocoService = inject(TranslocoService);
  dialog = inject(MatDialog);
  customDate = inject(CustomDatePipe);
  document = inject(DOCUMENT);

  reference = '';
  view?: WaitlistPortalPageDto;

  // blocks a second request while one is in flight, so a slot can't be booked twice
  busy = false;
  notFound = false;

  ngOnInit() {
    this.reference = this.route.snapshot.paramMap.get('reference') ?? '';

    if (!this.reference) {
      this.notFound = true;
      return;
    }

    this.load();
  }

  private load() {
    this.busy = true;
    this.waitlistService.loadEntry(this.reference).subscribe({
      next: view => this.apply(view),
      error: (error: HttpErrorResponse) => {
        this.busy = false;

        // a dead link gets its own screen: there is nothing the patient could retry
        if (error.status === 404) {
          this.notFound = true;
          return;
        }

        this.errorService.handleError(error, this.translocoService.translate('waitlist.error.title'));
      }
    });
  }

  private apply(view: WaitlistPortalPageDto) {
    this.view = view;
    this.busy = false;

    // this route sits outside the StartupComponent that sets the location's colors everywhere else
    this.setProperty('--custom-primary-color', view.appearance?.primaryColor);
    this.setProperty('--custom-secondary-color', view.appearance?.secondaryColor);
    this.setProperty('--custom-tertiary-color', view.appearance?.tertiaryColor);
  }

  private setProperty(propertyName: string, value?: string | null) {
    if (!value) {
      return;
    }

    this.document.documentElement.style.setProperty(propertyName, value);
  }

  // Every call answers with the refreshed view, so the page shows what the server did: declining or
  // booking one slot can change others too.
  private run(request: Observable<WaitlistPortalPageDto>) {
    this.busy = true;
    request.subscribe({
      next: view => this.apply(view),
      error: (error: HttpErrorResponse) => {
        this.busy = false;
        this.errorService.handleError(error, this.translocoService.translate('waitlist.error.title'));
        this.load();
      }
    });
  }

  bookReservation(offer: WaitlistPortalReservationDto) {
    this.confirm({
      icon: 'event_available',
      title: this.translocoService.translate('waitlist.confirm.book.title'),
      slot: offer,
      confirmLabel: this.translocoService.translate('waitlist.action.confirmBooking'),
      confirmIcon: 'check'
    }, () => this.run(this.waitlistService.bookSlot(this.reference, offer.employeeId, offer.from, offer.to)));
  }

  // Gives back the reservation, and the server rules the appointment out so it is not offered again.
  declineReservation() {
    this.confirm({
      icon: 'event_busy',
      title: this.translocoService.translate('waitlist.confirm.decline.title'),
      message: this.translocoService.translate('waitlist.confirm.decline.message'),
      confirmLabel: this.translocoService.translate('waitlist.action.confirmDecline'),
      confirmIcon: 'event_busy',
      destructive: true
    }, () => this.run(this.waitlistService.declineReservation(this.reference)));
  }

  bookSlot(slot: WaitlistPortalSlotDto) {
    const reservation = this.view?.reservation;

    this.confirm({
      icon: 'event_available',
      title: this.translocoService.translate('waitlist.confirm.book.title'),
      slot,
      // booking any slot releases the reservation, so say so up front
      notice: reservation ? this.releaseNotice(reservation) : undefined,
      confirmLabel: this.translocoService.translate('waitlist.action.confirmBooking'),
      confirmIcon: 'check'
    }, () => this.run(this.waitlistService.bookSlot(this.reference, slot.employeeId, slot.from, slot.to)));
  }

  private releaseNotice(reservation: WaitlistPortalReservationDto): string {
    const params = {
      date: this.customDate.transform(reservation.from, 'ccc, dd.MM.yyyy'),
      time: this.customDate.transform(reservation.from, 'HH:mm'),
      employee: reservation.employeeName
    };

    return reservation.employeeName
      ? this.translocoService.translate('waitlist.confirm.book.releasesReservation', params)
      : this.translocoService.translate('waitlist.confirm.book.releasesReservationNoEmployee', params);
  }

  leaveWaitlist() {
    const message = this.translocoService.translate('waitlist.confirm.leave.message');

    this.confirm({
      icon: 'logout',
      title: this.translocoService.translate('waitlist.confirm.leave.title'),
      message: this.view?.reservation
        ? `${this.translocoService.translate('waitlist.confirm.leave.releasesReservation')}\n${message}`
        : message,
      confirmLabel: this.translocoService.translate('waitlist.action.confirmLeave'),
      confirmIcon: 'logout',
      destructive: true
    }, () => this.run(this.waitlistService.cancel(this.reference)));
  }

  openSettings() {
    if (!this.view || this.busy) {
      return;
    }

    this.dialog.open(WaitlistSettingsDialog, {
      autoFocus: false,
      data: {notificationsEnabled: this.view.notificationsEnabled},
      width: '1046px',
      maxWidth: 'calc(100vw - 32px)',
      position: {bottom: '24px'},
      panelClass: 'waitlist-settings-panel'
    }).afterClosed().subscribe((enabled: unknown) => {
      if (typeof enabled === 'boolean') {
        this.run(this.waitlistService.setNotifications(this.reference, enabled));
      }
    });
  }

  private confirm(data: WaitlistConfirmData, onConfirmed: () => void) {
    if (this.busy) {
      return;
    }

    this.dialog.open(WaitlistConfirmDialog, {autoFocus: false, data, width: '520px', maxWidth: 'calc(100vw - 32px)'})
      .afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          onConfirmed();
        }
      });
  }

  // in the largest whole unit left ("2 days", then hours, then minutes), rounded down
  timeLeft(expiresAt: string): string {
    const left = Math.max(0, new Date(expiresAt).getTime() - Date.now());

    if (left >= DAY) {
      return this.countdown('days', Math.floor(left / DAY));
    }

    if (left >= HOUR) {
      return this.countdown('hours', Math.floor(left / HOUR));
    }

    return this.countdown('minutes', Math.max(1, Math.floor(left / MINUTE)));
  }

  private countdown(unit: 'days' | 'hours' | 'minutes', count: number): string {
    const key = count === 1 ? unit.slice(0, -1) : unit;
    return this.translocoService.translate(`waitlist.reservation.remaining.${key}`, {count});
  }

  get isClosed(): boolean {
    return this.view?.status === 'booked'
      || this.view?.status === 'cancelled'
      || this.view?.status === 'expired';
  }

  get closedMessageKey(): string {
    switch (this.view?.status) {
      case 'booked':
        return 'waitlist.closed.booked';
      case 'cancelled':
        return 'waitlist.closed.cancelled';
      default:
        return 'waitlist.closed.expired';
    }
  }
}
